import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import Reveal from "@/components/base/Reveal";
import Seo from "@/components/base/Seo";
import SectionHeading from "@/components/feature/SectionHeading";
import FeatureGrid from "@/components/feature/FeatureGrid";
import ProcessSteps from "@/components/feature/ProcessSteps";
import FaqAccordion from "@/components/feature/FaqAccordion";
import SolucoesCrossLink from "@/components/feature/SolucoesCrossLink";
import FinalCta from "@/pages/home/components/FinalCta";
import ServerPlans from "@/pages/servidores/components/ServerPlans";
import { whatsappLink, PLAN_IMAGE_DEDICATED } from "@/constants/site";
import {
  serverTypes,
  serverBenefits,
  serverUseCases,
  serverSteps,
  servidoresFaqs,
  dedicatedPlans,
} from "@/mocks/servidores";

const resourceBars = [
  { label: "Processamento", value: 68 },
  { label: "Memória", value: 54 },
  { label: "Armazenamento", value: 41 },
];

export default function Servidores() {
  return (
    <SiteLayout>
      <Seo
        title="Servidores e Infraestrutura para Empresas | NXHOST"
        description="Servidores dedicados, VPS, cloud e infraestrutura personalizada com servidores no Brasil para projetos que exigem mais desempenho, segurança e estabilidade."
        keywords="servidores, servidor dedicado, VPS, cloud, infraestrutura, hospedagem, servidores no Brasil"
        path="/servidores"
        titleEn="Servers and Infrastructure for Companies | NXHOST"
        titleEs="Servidores e Infraestructura para Empresas | NXHOST"
        descriptionEn="Dedicated servers, VPS, cloud and custom infrastructure with servers in Brazil for projects that demand more performance, security and stability."
        descriptionEs="Servidores dedicados, VPS, cloud e infraestructura personalizada con servidores en Brasil para proyectos que exigen más rendimiento, seguridad y estabilidad."
        breadcrumbs={[
          { name: "Início", path: "/" },
          { name: "Servidores", path: "/servidores" },
        ]}
        plans={dedicatedPlans.map((plan) => ({
          name: plan.name,
          price: plan.price,
          url: "https://nxhost.com.br/servidores",
          description: plan.audience,
          image: PLAN_IMAGE_DEDICATED,
          category: "Servidores dedicados",
        }))}
        faqs={servidoresFaqs}
      />

      <PageHero
        badge="Servidores e infraestrutura"
        badgeIcon="ri-server-line"
        title="Servidores para projetos que exigem mais estrutura."
        subtitle="Quando a hospedagem comum já não é suficiente, a NXHOST oferece servidores e infraestrutura robusta para aplicações que precisam de desempenho, segurança e controle."
        crumbs={[{ label: "Início", to: "/" }, { label: "Servidores" }]}
      >
        <Reveal delay={220}>
          <div className="rounded-2xl border border-background-50/10 bg-foreground-900/60 p-5 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wide text-background-400">
                Infraestrutura
              </span>
              <span className="flex items-center gap-1.5 text-[11px] font-medium text-secondary-400">
                <span className="h-2 w-2 rounded-full bg-secondary-400"></span>
                Estável
              </span>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-background-50/10 bg-background-50/5 p-3">
                <p className="font-heading text-2xl font-bold text-background-50">Alta</p>
                <p className="mt-1 text-[11px] text-background-300">Disponibilidade</p>
              </div>
              <div className="rounded-lg border border-background-50/10 bg-background-50/5 p-3">
                <p className="font-heading text-2xl font-bold text-background-50">BR</p>
                <p className="mt-1 text-[11px] text-background-300">Servidores no Brasil</p>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {resourceBars.map((bar) => (
                <div key={bar.label}>
                  <div className="mb-1.5 flex items-center justify-between text-[11px] text-background-400">
                    <span>{bar.label}</span>
                    <span>Configurável</span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-background-50/10">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-primary-500 to-accent-400"
                      style={{ width: `${bar.value}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </PageHero>

      <section className="bg-background-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Opções de infraestrutura"
            badgeIcon="ri-hard-drive-3-line"
            title="Escolha a estrutura certa para o seu projeto."
            description="Cada projeto tem uma necessidade diferente. Reunimos opções de servidores para atender de aplicações web a operações de alto volume."
          />
          <div className="mt-12">
            <FeatureGrid items={serverTypes} columns={4} accent="primary" />
          </div>
        </div>
      </section>

      <ServerPlans />

      <section className="bg-background-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Benefícios"
            badgeIcon="ri-shield-star-line"
            badgeTone="secondary"
            title="Mais do que um servidor: uma base confiável."
            description="Uma estrutura pensada para manter o seu projeto rápido, seguro e disponível, com acompanhamento de quem entende de infraestrutura."
          />
          <div className="mt-12">
            <FeatureGrid items={serverBenefits} columns={3} accent="secondary" />
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-foreground-950 py-16 text-background-50 md:py-24">
        <div className="absolute inset-0 nx-grid opacity-25"></div>
        <div className="nx-glow absolute -top-20 right-1/4 h-[420px] w-[420px] animate-pulseGlow"></div>
        <div className="relative mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Para quem é"
            badgeIcon="ri-focus-3-line"
            title="Projetos que não podem ficar fora do ar."
            description="Servidores e infraestrutura dedicada para operações que dependem de desempenho e estabilidade todos os dias."
            tone="dark"
          />
          <div className="mt-12">
            <FeatureGrid items={serverUseCases} tone="dark" columns={4} accent="accent" />
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Como funciona"
            badgeIcon="ri-route-line"
            badgeTone="accent"
            title="Do diagnóstico à operação, ao seu lado."
            description="A gente entende a sua demanda, dimensiona a estrutura e acompanha a operação para tudo funcionar como deveria."
          />
          <div className="mt-12">
            <ProcessSteps steps={serverSteps} />
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Dúvidas frequentes"
            badgeIcon="ri-question-line"
            title="Perguntas sobre servidores e infraestrutura."
          />
          <div className="mt-12">
            <FaqAccordion items={servidoresFaqs} />
          </div>

          <Reveal>
            <div className="mx-auto mt-10 flex max-w-3xl flex-col items-center gap-4 rounded-xl border border-background-200 bg-background-50 p-6 text-center sm:flex-row sm:justify-between sm:text-left">
              <div>
                <p className="font-heading text-base font-semibold text-foreground-950">
                  Não sabe qual servidor é ideal?
                </p>
                <p className="mt-1 text-sm text-foreground-600">
                  Conte o que você precisa e ajudamos a dimensionar a sua estrutura.
                </p>
              </div>
              <a
                href={whatsappLink("Ola! Preciso de ajuda para escolher um servidor da NXHOST.")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-customer-service-2-line text-lg" aria-hidden="true"></i>
                Falar com um especialista
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <SolucoesCrossLink
        title="Infraestrutura e soluções digitais em um só parceiro"
        description="Da estrutura ao resultado: a NXHOST cuida de servidores, hospedagem, criação de sites e da estratégia digital que coloca sua empresa no Google e gera oportunidades."
        links={[
          { label: "VPS", to: "/vps" },
          { label: "Hospedagem", to: "/hospedagem" },
          { label: "Criação de Sites", to: "/sites" },
          { label: "SEO", to: "/solucoes#seo" },
        ]}
      />

      <FinalCta />
    </SiteLayout>
  );
}